import * as Sentry from "@sentry/browser";
import * as React from "react";
import { UserInfo } from "@app/user/UserContext";
import { Unauthorized, NotFound, Forbidden, ServerError, HomepageError } from "@app/shared/ErrorPages";

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  user?: UserInfo;
}

export interface ErrorBoundaryState {
  errorPage?: string;
  hasError: boolean;
  stack?: string;
  status?: number;
  statusText?: string;
  traceId?: string;
}

/**
 * ErrorBoundary catches any errors below itself in React's component tree that are not otherwise handled.
 * We intentionally only use it at the page level so we can replace entire pages with appropriate error messages,
 * and so something is very obviously wrong and users will know when to complain.
 * There's no type enforcement on errors since throw isn't a function, but ErrorBoundary expects either:
 * 1) an object with status, statusText, and traceId (see ErrorBoundaryState above)
 * 2) a number (i.e. a status code by itself)
 * That way it knows which error page to render. Otherwise, it will default to our generic ServerError page ("Sorry! Something went wrong on our end.").
 * For whatever reason, just passing the XHR response object doesn't work
 */
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
    };
  }

  //public componentDidMount() {
  //  window.addEventListener("unhandledrejection", this.promiseRejectionHandler);
  //}

  //public componentWillUnmount(): void {
  //  window.removeEventListener("unhandledrejection", this.promiseRejectionHandler);
  //}

  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (process.env.NODE_ENV !== "development") {
      Sentry.withScope((scope) => {
        // Attach the error and its information.
        Object.keys(info).forEach((key) => {
          scope.setExtra(key, info[key as keyof React.ErrorInfo]);
        });
        Sentry.captureException(error);

        // If we've got a user, set that too.
        const { user } = this.props;
        if (user) {
          scope.setUser({
            email: user.email,
            id: user.userId && user.userId.toString() || null,
            username: user.name,
          });
        }
      });
    }
  }

  static getDerivedStateFromError(error: number | Partial<ErrorBoundaryState>): ErrorBoundaryState {
    const errorPage = (typeof error === 'object' && error?.stack?.includes('Home.render')) ? 'home' : null;

    return {
      errorPage: errorPage,
      hasError: true,
      status: (error as Partial<ErrorBoundaryState>)?.status || error as number,
      statusText: (error as Partial<ErrorBoundaryState>)?.statusText,
      traceId: (error as Partial<ErrorBoundaryState>)?.traceId,
    };
  }

  public render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.status === 401) {
      return <Unauthorized />;
    } else if (this.state.status === 404) {
      return <NotFound />;
    } else if (this.state.status === 403) {
      return <Forbidden />;
    } else if (this.state.status >= 500 && this.state.status < 600) {
      return <ServerError traceId={this.state.traceId} />;
    } else if (this.state.errorPage === 'home') {
      if (this.props?.user?.userId) {
        localStorage.removeItem(`${this.props.user.userId}project_filters`);
      }

      return <HomepageError />;
    } else {
      // this is probably due to a UI runtime error, but the Server Error message will suffice for the user
      // React will spit out an object with errorText that, if supplied, should help troubleshoot
      return <ServerError />;
    }

    return this.props.children;
  }

  //private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
  //  this.setState({
  //    hasError: true,
  //    ...event.reason,
  //  });
  //}
}
